import first from "lodash/first";
import sortBy from "lodash/sortBy";
import { FC, JSX, useContext, useEffect, useRef, useState } from "react";
import { Redirect } from "react-router-dom";
import { CodeMapSet } from "@app/codemaps";
import { ClientInsuranceModel, InsuranceList } from "@app/insurance";
import { MemberSetModel } from "@app/members";
import CreateProject from "@app/projects/create/createProject";
import SpecialtyGroupsTable from "@app/projects/create/SpecialtyGroupsTable";
import WizardStep from "@app/projects/create/WizardStep";
import { TimeDistanceCalculatorType } from "@app/projects/enumerators";
import { timeDistanceCalculatorTypeDisplayMap } from "@app/projects/timeDistanceCalculatorTypeDisplayMap";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import InfoIndicator from "@app/shared/InfoIndicator";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { TemplateCollectionMetadata, TemplateSpecialtyGroup } from "@app/templates";
import { AccuracySpecialtyDefinition, AccuracyTemplateCollectionMetadata } from "@app/templates/accuracy/models";
import TemplateService from "@app/templates/templateService";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";

interface CreateProjectStepTemplateProps {
  accuracyIsLoading: boolean;
  accuracyTemplateCollections: AccuracyTemplateCollectionMetadata[];
  accuracySpecialtyDefinitions: AccuracySpecialtyDefinition[];
  authorizedInsurances: ClientInsuranceModel[];
  authorizedInsurancesErred: boolean;
  baseUrl: string;
  completionTracker: boolean[];
  createProjectModel: CreateProject;
  handleAccessCodeMapSetChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleAccuracyTemplateCollectionsChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleAccuracyTemplateCorrelationIdsChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleExpandSpecialtyMileageChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleAccuracySpecialtyDefChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleAddInspectedInsurance: () => void;
  handleAuthorizedInsurancesChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleMarketSpecialtyCodeMapSetChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleNetworkSpecialtyCodeMapSetChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleRemoveInspectedInsurance: (uid: string) => void;
  handleSpecialtyGroupsChange: (selectedRecords: TemplateSpecialtyGroup[]) => void;
  handleSpecialtyToQdpCodeMapSetChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleTelehealthSpecialtyGroupsChange: (selectedSpecialtyGroupCode: string[]) => void;
  handleTemplateCollectionsChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleTemplateCorrelationIdsChange: (e: React.ChangeEvent<HTMLSelectElement>, userRoles: string[]) => void;
  handleTimeDistanceCalculationTypeChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleUseAccuracyChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleUseAdequacyChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isZipProject: boolean;
  originalTemplateCollections: TemplateCollectionMetadata[];
  progressList: string[];
  projectAccessCodeMapSets: CodeMapSet[];
  projectCollections: TemplateCollectionMetadata[];
  projectTemplateCollections: TemplateCollectionMetadata[];
  expandSpecialtyMileageCodeMapSets: CodeMapSet[];
  selectedInsurances: ClientInsuranceModel[];
  selectedInsuranceUid: string;
  showNetworkSpecialtyCodeMap: boolean;
  specialtyCodeMapSets: CodeMapSet[];
  specialtyDefinitionsMissing: boolean;
  specialtyGroups: TemplateSpecialtyGroup[];
  specialtyToQdpCodeMapSets: CodeMapSet[];
  timeDistanceCalculatorTypes: TimeDistanceCalculatorType[];
  useAccuracy: boolean;
  useAdequacy: boolean;
  accessCodeMapDefaultSetId: number;
  currentIndex: number;
}

const CreateProjectStepTemplate: FC<CreateProjectStepTemplateProps> = (props: CreateProjectStepTemplateProps) => {
  const user = useContext(UserContext);
  const sortOrder = useRef<Array<keyof TemplateSpecialtyGroup>>(["displayOrder"]).current;
  const templateService = useRef(new TemplateService()).current;

  const [restartApp] = useState(!props.completionTracker.filter((_, index) => (props.currentIndex - 1) > index).every(step => step));
  const [sortedSpecialtyGroups, setSortedSpecialtyGroups] = useState(props.specialtyGroups && sortBy(props.specialtyGroups, sortOrder) || props.specialtyGroups);
  const [priorityTemplates, setPriorityTemplates] = useState<string[]>([]);

  useEffect(() => {
    setSortedSpecialtyGroups(props.specialtyGroups && sortBy(props.specialtyGroups, sortOrder) || props.specialtyGroups);
  }, [props.specialtyGroups]);

  useEffect(() => {
    templateService.getPriorityCmsTemplates().then((newPriorityTemplates) => {
      setPriorityTemplates(newPriorityTemplates);
    });
  }, []);

  const calculatorTypeRolesMap: { [key: string]: Roles[] } = {
    [TimeDistanceCalculatorType.Unknown]: undefined,
    [TimeDistanceCalculatorType.Quest]: undefined,
    [TimeDistanceCalculatorType.GeographyAware]: undefined,
    [TimeDistanceCalculatorType.GraphhopperRouting]: [Roles.ROUTING_DISTANCE_USER],
    [TimeDistanceCalculatorType.StraightLine]: [Roles.STRAIGHT_LINE_DISTANCE_USER],
  };

  if (restartApp) {
    return <Redirect to={`${props.baseUrl}/1`} />;
  }

  const sortedTimeDistanceCalculatorTypes = props.timeDistanceCalculatorTypes.sort(
    (a, b) => timeDistanceCalculatorTypeDisplayMap[a].localeCompare(timeDistanceCalculatorTypeDisplayMap[b]));

  const timeDistanceCalculatorTypeDefault = (props.createProjectModel && props.createProjectModel.timeDistanceCalculatorType)
    ? props.createProjectModel.timeDistanceCalculatorType.toString()
    : sortedTimeDistanceCalculatorTypes.length > 0
      ? sortedTimeDistanceCalculatorTypes.find(t => t === TimeDistanceCalculatorType.Quest) ?? sortedTimeDistanceCalculatorTypes[0]
      : TimeDistanceCalculatorType.Unknown;

  const projectTemplateCollectionDefault = (props.createProjectModel && props.createProjectModel.adequacyTemplateCollectionId)
    ? props.createProjectModel.adequacyTemplateCollectionId.toString()
    : 0;
  const projectTemplateCorrelationIdDefault =
    (props.createProjectModel && props.createProjectModel.adequacyTemplateCorrelationId)
      ? props.createProjectModel.adequacyTemplateCorrelationId
      : 0;

  let renderProjectTemplateCorrelationIdsList: JSX.Element;
  let renderAccessCodeMapsList: JSX.Element;
  let renderNetworkSpecialtyCodeMapsList: JSX.Element;
  let renderMarketSpecialtyCodeMapsList: JSX.Element;

  const renderTimeDistanceCalculationTypeList: JSX.Element = (
    <QESSelect
      label="Time/Distance Calculation Type"
      className="form-control"
      value={timeDistanceCalculatorTypeDefault}
      testId="create-project-step-template__adequacy-time-distance-calculation-type-list-select"
      onChange={props.handleTimeDistanceCalculationTypeChange}
    >
      {sortedTimeDistanceCalculatorTypes.map((option) => calculatorTypeRolesMap[option] ? (<RoleRestricted key={option} allowedRoles={calculatorTypeRolesMap[option]}><option key={option} value={option}>{timeDistanceCalculatorTypeDisplayMap[option]}</option></RoleRestricted>) : (<option key={option} value={option}>{timeDistanceCalculatorTypeDisplayMap[option]}</option>))}
    </QESSelect>
  );

  const renderProjectTemplateCollectionsList = (
    <QESSelect
      label="Adequacy Template Collection"
      className="form-control"
      value={projectTemplateCollectionDefault}
      testId="create-project-step-template__adequacy-template-collections-list-select"
      onChange={props.handleTemplateCollectionsChange}
    >
      <option value="0">Select an Adequacy Template Collection</option>
      {props.projectCollections?.sort((a, b) => a.resource.name > b.resource.name ? 1 : -1).map((option) => (
        <option key={option.resource.resourceId} value={option.resource.resourceId}>{`${option.resource.name} - ${option.resource.description}`}</option>
      ))}
    </QESSelect>
  );

  if (props.createProjectModel.adequacyTemplateCollectionId) {
    const selectedProjectCollection = first(props.projectCollections.filter((item) => item.templateCollectionId === props.createProjectModel.adequacyTemplateCollectionId));
    const priorityTemplatesToInclude = priorityTemplates.filter((pt) => selectedProjectCollection?.correlationIds.includes(pt)) || [];
    const sortedNonPriorityTemplates = selectedProjectCollection?.correlationIds.sort().filter((t) => !priorityTemplates.includes(t)) || [];

    renderProjectTemplateCorrelationIdsList = (
      <QESSelect
        label="Adequacy Template"
        className="form-control"
        value={projectTemplateCorrelationIdDefault}
        testId="create-project-step-template__adequacy-template-correlation-ids-list-select"
        onChange={(e) => props.handleTemplateCorrelationIdsChange(e, user.roles)}
      >
        <option value="0">Select an Adequacy Template</option>
        {priorityTemplatesToInclude.length > 0 && (
          <optgroup label="New Templates">
            {
              priorityTemplatesToInclude.map((t) => (
                <option key={t} value={t}>{`${t}`}</option>
              ))
            }
          </optgroup>
        )}
        {sortedNonPriorityTemplates.map((option) => (
          <option key={option} value={option}>{`${option}`}</option>
        ))}
      </QESSelect>
    );
  } else {
    renderProjectTemplateCorrelationIdsList = (<QESSelect label="Adequacy Template" testId="Loading" className="form-control"><option key="0" value="0">Select an Adequacy Template</option></QESSelect>);
  }

  //==========
  // Accuracy
  //==========
  let renderAccuracyTemplateCollectionsList: React.ReactNode;
  let renderAccuracyTemplateCorrelationIdsList: React.ReactNode;
  let renderAccuracySpecialtyDefinitionList: React.ReactNode;
  let renderAuthorizedInsurancesList: React.ReactNode;

  const hasAccuracyTemplates = props.accuracyTemplateCollections && props.accuracyTemplateCollections.length > 0;
  if (props.useAccuracy && hasAccuracyTemplates) {
    const getOptionValue = (option: AccuracyTemplateCollectionMetadata) => option.resource && option.resource.name || option.templateCollectionId;
    renderAccuracyTemplateCollectionsList = (
      <QESSelect
        label="Accuracy Template Collection"
        className="form-control"
        value={props.createProjectModel.accuracyTemplateCollectionId || ""}
        testId="create-project-step-template__accuracy-template-collections-list-select"
        onChange={props.handleAccuracyTemplateCollectionsChange}
      >
        <option key="<none>" value="">Select an Accuracy Template Collection</option>
        {props.accuracyTemplateCollections.sort((a, b) => getOptionValue(a) > getOptionValue(b) ? 1 : -1).map(
          (option) => (
            <option key={option.templateCollectionId} value={option.templateCollectionId}>{getOptionValue(option)
            }</option>
          ))}
      </QESSelect>
    );

    renderAuthorizedInsurancesList = (
      <>
        <div className="d-flex justify-content-between align-items-end">
          <QESSelect
            label="Inspected Insurances"
            containerClassName="w-100"
            testId="create-project-step-template__inspected-insurances"
            className="form-control"
            onChange={props.handleAuthorizedInsurancesChange}
          >
            <option key="<none>" value="">Select an insurance</option>
            {props.authorizedInsurances.filter((ins) =>
              props.selectedInsurances.findIndex((lookup) => lookup.uid === ins.uid) === -1).map(
                (option) => (
                  <option key={option.uid} value={option.uid}>{option.planName}: {option.uid}</option>))}
          </QESSelect>
          <QESButton qesTestId="create-project-step-template_plus-icon" qesType="primary" onClick={() => props.handleAddInspectedInsurance()}>
            <QesIcon iconName={QesIcons.Plus} description="Add" className="align-text-top" />
          </QESButton>
        </div>
        <InsuranceList selectedInsurances={props.selectedInsurances} handleRemoveInspectedInsurance={props.handleRemoveInspectedInsurance} />
      </>
    );

    const selectedTemplateCollection = props.accuracyTemplateCollections.find((t) => t.templateCollectionId === props.createProjectModel.accuracyTemplateCollectionId);
    const correlationIds = (selectedTemplateCollection && selectedTemplateCollection.correlationIds) || [];

    renderAccuracyTemplateCorrelationIdsList = (
      <QESSelect
        label="Accuracy Template"
        className="form-control"
        value={props.createProjectModel.accuracyTemplateCorrelationId || ""}
        testId="create-project-step-template__accuracy-template-correlation-ids-list-select"
        onChange={props.handleAccuracyTemplateCorrelationIdsChange}
      >
        <option key="<none>" value="">Select an Accuracy Template</option>
        {correlationIds.sort().map((option) => (
          <option key={option} value={option}>{option}</option>
        ))}
      </QESSelect>
    );

    renderAccuracySpecialtyDefinitionList = (
      <QESSelect
        label="Accuracy Specialty Definition"
        className="form-control"
        value={props.createProjectModel.accuracySpecialtyDefinitionId || ""}
        testId="create-project-step-template__accuracy-specialty-definition-list-select"
        onChange={props.handleAccuracySpecialtyDefChange}
      >
        <option key="<none>" value="">Select an Accuracy Specialty Definition</option>
        {props.accuracySpecialtyDefinitions.sort((a, b) =>
          a.name > b.name ? 1 : -1).map((option) => (
            <option key={option.specialtyDefinitionId} value={option.specialtyDefinitionId}>{option.name}</option>
          ))}
      </QESSelect>);
  }

  const sortArrayAfterFirstItem = (arr: Array<MemberSetModel | CodeMapSet>): Array<MemberSetModel | CodeMapSet> => {
    let newArr = arr;
    const firstItem = newArr.shift();
    newArr = newArr.sort((a, b) => a.resource.name > b.resource.name ? 1 : -1);
    newArr.unshift(firstItem);
    return newArr;
  }

  // A template must be chosen before displaying anything about a codeMap
  if (props.projectAccessCodeMapSets && props.projectAccessCodeMapSets.length > 0) {
    renderAccessCodeMapsList = (
      <QESSelect
        label="Access Region to Classification Code Map"
        className="form-control"
        onChange={props.handleAccessCodeMapSetChange}
        testId="create-project-step-template__access-region-to-classification-code-map-select"
        value={props.createProjectModel.accessCodeMapSetId ? props.createProjectModel.accessCodeMapSetId : ""}
      >
        {props.projectAccessCodeMapSets[0].codeMapSetId === props.accessCodeMapDefaultSetId ? props.createProjectModel.accessCodeMapSetId : <option value="0"> Select an Access Region to Classification Code Map</option>}
        {(sortArrayAfterFirstItem(props.projectAccessCodeMapSets) as CodeMapSet[]).map((set) => (
          set.codeMapSetVersions.some((setversion) => setversion.codeMapSetVersionStatus === "activated") ?
            <option key={set.codeMapSetId} value={set.codeMapSetId}>{set.resource.name}</option> :
            <option key={set.codeMapSetId} value={set.codeMapSetId} disabled={true}>{set.resource.name} - No Activated Versions</option>
        ))}
      </QESSelect>
    );
  }

  const renderExpandSpecialtyMileage = (
    <QESSelect
      label={
        <span>
          Expand Specialty Mileage {" "}
          {
            props.expandSpecialtyMileageCodeMapSets && props.expandSpecialtyMileageCodeMapSets.length > 0 &&
            <InfoIndicator tooltipTitle="Performance Impact - Intended for Nebraska Medicaid: Apply Codemap to expand provider search to 500 county miles." />
          }
        </span>
      }
      className="form-control"
      disabled={!(props.expandSpecialtyMileageCodeMapSets && props.expandSpecialtyMileageCodeMapSets.length > 0)}
      onChange={props.handleExpandSpecialtyMileageChange}
      testId="create-project-step-template__expand-specialty-mileage-select"
      value={props.createProjectModel.expandSpecialtyMileageCodeMapSetId ? props.createProjectModel.expandSpecialtyMileageCodeMapSetId : "0"}
    >
      {props.expandSpecialtyMileageCodeMapSets && props.expandSpecialtyMileageCodeMapSets.length > 0 && (
        <option value="0" key="0">Select an Expand Specialty Mileage</option>
      )}
      {props.expandSpecialtyMileageCodeMapSets.sort((a, b) => a.resource.name > b.resource.name ? 1 : -1).map((set) => (
        set.codeMapSetVersions.some((setversion) => setversion.codeMapSetVersionStatus === "activated") ?
          <option key={set.codeMapSetId} value={set.codeMapSetId}>{set.resource.name}</option> :
          <option key={set.codeMapSetId} value={set.codeMapSetId} disabled={true}>{set.resource.name} - No Activated Versions</option>
      ))}
    </QESSelect>
  );

  if (props.specialtyCodeMapSets && props.specialtyCodeMapSets.length > 0) {
    renderNetworkSpecialtyCodeMapsList = (
      <QESSelect
        label="Network Specialty Code Map"
        className="form-control"
        onChange={props.handleNetworkSpecialtyCodeMapSetChange}
        testId="create-project-step-template__network-specialty-code-maps-list-select"
        value={props.createProjectModel.networkSpecialtyCodeMapSetId ? props.createProjectModel.networkSpecialtyCodeMapSetId : 0}
      >
        <option value={null}>Select a Network Specialty Code Map</option>
        {props.specialtyCodeMapSets.sort((a, b) => a.resource.name > b.resource.name ? 1 : -1).map((set) => (
          set.codeMapSetVersions.some((sv) => sv.codeMapSetVersionStatus === "activated") ?
            <option key={set.codeMapSetId} value={set.codeMapSetId}>{set.resource.name}</option> :
            <option key={set.codeMapSetId} value={set.codeMapSetId} disabled={true}>{set.resource.name} - No Activated Versions</option>
        ))}
      </QESSelect>
    );

    renderMarketSpecialtyCodeMapsList = (
      <QESSelect
        label="Market Specialty Code Map"
        className="form-control"
        onChange={props.handleMarketSpecialtyCodeMapSetChange}
        testId="create-project-step-template__market-specialty-code-maps-list-select"
        value={props.createProjectModel.marketSpecialtyCodeMapSetId ? props.createProjectModel.marketSpecialtyCodeMapSetId : 0}
      >
        <option value={null}>Select a Market Specialty Code Map</option>
        {props.specialtyCodeMapSets.sort((a, b) => a.resource.name > b.resource.name ? 1 : -1).map((set) => (
          set.codeMapSetVersions.some((sv) => sv.codeMapSetVersionStatus === "activated") ?
            <option key={set.codeMapSetId} value={set.codeMapSetId}>{set.resource.name}</option> :
            <option key={set.codeMapSetId} value={set.codeMapSetId} disabled={true}>{set.resource.name} - No Activated Versions</option>
        ))}
      </QESSelect>
    );
  }

  const renderSpecialtyToQdpCodeMapsList = (
    <QESSelect
      label="Specialty to BDX UID Code Map"
      className="form-control"
      testId="create-project-step-template__specialty-to-qdp-code-maps-list-select"
      onChange={props.handleSpecialtyToQdpCodeMapSetChange}
      value={props.createProjectModel.accuracySpecialtyToQdpCodeMapSetId ? props.createProjectModel.accuracySpecialtyToQdpCodeMapSetId : ""}
    >
      <option value={null}>Select a Specialty to BDX UID Code Map</option>
      {props.specialtyToQdpCodeMapSets.sort((a, b) => a.resource.name > b.resource.name ? 1 : -1).map((set) => (
        set.codeMapSetVersions.some((sv) => sv.codeMapSetVersionStatus === "activated") ?
          <option key={set.codeMapSetId} value={set.codeMapSetId}>{set.resource.name}</option> :
          <option key={set.codeMapSetId} value={set.codeMapSetId} disabled={true}>{set.resource.name} - No Activated Versions</option>
      ))}
    </QESSelect>
  );

  const shouldRenderCodeMapDropdowns =
    props.specialtyCodeMapSets &&
    props.specialtyCodeMapSets.length > 0 &&
    !!props.createProjectModel.adequacyTemplateCorrelationId &&
    props.createProjectModel.adequacyTemplateCorrelationId !== "0";

  const handleIsNextDisabled = () => {
    //user must select at least adequacy or accuracy
    if (!props.createProjectModel.accuracyTemplateCollectionId &&
      !props.createProjectModel.adequacyTemplateCollectionId) {
      return true;
    }

    //user selected Accuracy template collection but no accuracy template
    if (props.createProjectModel.accuracyTemplateCollectionId &&
      !props.createProjectModel.accuracyTemplateCorrelationId) {
      return true;
    }

    //user selected Accuracy template collection but no code map set
    if (props.createProjectModel.accuracyTemplateCollectionId &&
      !props.createProjectModel.accuracySpecialtyToQdpCodeMapSetId) {
      return true;
    }

    //user selected adequacy template collection but no adequacy template
    if (props.createProjectModel.adequacyTemplateCollectionId &&
      !props.createProjectModel.adequacyTemplateCorrelationId) {
      return true;
    }

    //if accuracy-only project, ensure specialty definition is selected
    if (props.createProjectModel.accuracyTemplateCollectionId &&
      !props.createProjectModel.adequacyTemplateCollectionId &&
      !props.createProjectModel.accuracySpecialtyDefinitionId &&
      !sortedSpecialtyGroups ||
      sortedSpecialtyGroups.length === props.createProjectModel.excludedSpecialtyGroupCodes.length) {
      return true;
    }

    //if adequacy is selected, validate the rest of its fields
    if (props.createProjectModel.adequacyTemplateCollectionId &&
      ((props.projectAccessCodeMapSets &&
        props.projectAccessCodeMapSets.length > 0 &&
        !props.createProjectModel.accessCodeMapSetId) ||
        !sortedSpecialtyGroups ||
        sortedSpecialtyGroups.length === props.createProjectModel.excludedSpecialtyGroupCodes.length ||
        !props.createProjectModel.timeDistanceCalculatorType || props.createProjectModel.timeDistanceCalculatorType === undefined)) {
      return true;
    }
  }

  return (
    <WizardStep {...props} heading="Create a New Project" subheading="Select a template..." baseUrl={props.baseUrl} prevUrl={(props.currentIndex - 1).toString()} nextUrl={(props.currentIndex + 1).toString()} progressList={props.progressList} isNextDisabled={handleIsNextDisabled()}>
      <RoleRestricted allowedRoles={[Roles.ACCURACY_LICENSE_TIER_1]}>
        {hasAccuracyTemplates && !props.isZipProject && (
          <>
            {props.specialtyDefinitionsMissing && (
              <QESAlert type="danger" classNames="mb-2" persist={true}>
                Could not find Specialty Definitions in the database
              </QESAlert>
            )}

            {props.authorizedInsurancesErred && (
              <QESAlert type="danger" classNames="mb-2" persist={true}>
                Unable to load available accuracy insurance plans at this time.
              </QESAlert>
            )}

            <div className="form-group row">
              <div className="col-4 offset-2 d-flex">
                <QESCheckbox
                  containerClassName="checkbox align-self-center"
                  label="Accuracy Analysis"
                  checked={props.useAccuracy && !props.isZipProject}
                  onChange={props.handleUseAccuracyChange}
                  testId="create-project-step-template__accuracy-analysis-checkbox"
                />
              </div>
            </div>

            {props.accuracyIsLoading && (
              <div className="col-4 offset-2 d-flex">
                <LoadingSpinner />
              </div>
            )}

            {props.useAccuracy && (
              <>
                <div className="form-group row">
                  <div className="col-4 offset-2">
                    {renderAccuracyTemplateCollectionsList}
                  </div>
                </div>

                <div className="form-group row">
                  <div className="col-4 offset-2">
                    {renderAccuracyTemplateCorrelationIdsList}
                  </div>
                </div>
              </>
            )}

            {props.useAccuracy && props.specialtyToQdpCodeMapSets && props.specialtyToQdpCodeMapSets.length > 0 &&
              <div className="form-group row">
                <div className="col-4 offset-2">
                  {renderSpecialtyToQdpCodeMapsList}
                </div>
              </div>
            }

            {props.useAccuracy && !props.useAdequacy && (
              <>
                <div className="form-group row">
                  <div className="col-4 offset-2">
                    {renderAccuracySpecialtyDefinitionList}
                  </div>
                </div>
              </>
            )}

            {props.useAccuracy && !props.useAdequacy
              && props.specialtyCodeMapSets && props.specialtyCodeMapSets.length > 0 && (
                <>
                  <div className="form-group row">
                    <div className="col-4 offset-2">
                      {renderMarketSpecialtyCodeMapsList}
                    </div>
                  </div>
                </>
              )}

            {
              props.useAccuracy && (
                <div className="form-group row">
                  <div className="col-4 offset-2">
                    {renderAuthorizedInsurancesList}
                  </div>
                </div>
              )
            }

            <div className="form-group row">
              <div className="col-4 offset-2">
                <hr />
              </div>
            </div>

            <div className="form-group row">
              <div className="col-4 d-flex offset-2">
                <QESCheckbox
                  containerClassName="checkbox align-self-center"
                  label="Adequacy Analysis"
                  checked={props.useAdequacy}
                  testId="create-project-step-template__adequacy-analysis-checkbox"
                  onChange={props.handleUseAdequacyChange}
                />
              </div>
            </div>
          </>
        )}
      </RoleRestricted>

      {props.useAdequacy && (
        <>
          <div className="form-group row">
            <div className="col-4 offset-2">
              {renderProjectTemplateCollectionsList}
            </div>
          </div>
          <div className="form-group row">
            <div className="col-4 offset-2">
              {renderProjectTemplateCorrelationIdsList}
            </div>
          </div>

          {!props.isZipProject && (
            <div className="form-group row">
              <div className="col-4 offset-2">
                {renderExpandSpecialtyMileage}
              </div>
            </div>
          )}
          <div className="form-group row">
            <div className="col-4 offset-2">
              {renderTimeDistanceCalculationTypeList}
            </div>
          </div>
        </>
      )}

      {props.projectAccessCodeMapSets && props.projectAccessCodeMapSets.length > 0 &&
        <div className="form-group row">
          <div className="col-4 offset-2">
            {renderAccessCodeMapsList}
          </div>
        </div>
      }

      {shouldRenderCodeMapDropdowns && (
        <>
          {props.showNetworkSpecialtyCodeMap && !props.isZipProject && (
            <div className="form-group row">
              <div className="col-4 offset-2">
                {renderNetworkSpecialtyCodeMapsList}
              </div>
            </div>
          )}

          <div className="form-group row">
            <div className="col-4 offset-2">
              {renderMarketSpecialtyCodeMapsList}
            </div>
          </div>
        </>
      )}

      {!!sortedSpecialtyGroups && sortedSpecialtyGroups.length > 0 && (
        <div className="form-group row">
          <div className="col-8">
            <SpecialtyGroupsTable
              canEdit={true}
              excludedSpecialtyGroupCodes={props.createProjectModel.excludedSpecialtyGroupCodes}
              handleSpecialtyGroupsChange={props.handleSpecialtyGroupsChange}
              handleTelehealthSpecialtyGroupsChange={props.handleTelehealthSpecialtyGroupsChange}
              specialtyGroups={sortedSpecialtyGroups}
              telehealthSpecialtyGroupCodes={props.createProjectModel.telehealthSpecialtyGroupCodes}
            />
          </div>
        </div>
      )}
    </WizardStep>
  );
}

export default CreateProjectStepTemplate;
